import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { z } from 'zod';
import {
  SOURCE_ADMIN_SMS,
  SOURCE_RCS,
  SOURCE_TCAPI,
} from '@rsos/constants/locationSources';
import { useFlag } from '@rsos/flags';
import { StreamWSMessage } from '@rsos/hermes-js/src/typings/common';
import {
  CONVERSATION_END,
  CONVERSATION_START,
  CONVERSATION_TRANSFER,
  CONVERSATION_UPDATE,
  ConversationMessage,
} from '@rsos/hermes-js/src/typings/conversation';
import {
  addConversation,
  setConversationTransfer,
  updateConversation,
} from '@rsos/reduxDataStore/src/features/conversations/conversationsSlice';
import { addRCSConversation } from '@rsos/reduxDataStore/src/features/rcs/rcsSlice';
import { dismissToastByID } from '@rsos/utils/toastUtils';
import { addLocToQueue } from '../../../actions';
import useHasAccessToAdminLineRequests from '../../../hooks/useHasAccessToAdminLineRequests';
import { convertKeysToCamelCase } from './helpers';

const conversationTransferMessageSchema = z.object({
  type: z.literal(CONVERSATION_TRANSFER),
  body: z
    .object({
      caller_id: z.string(),
      conversation_id: z.string(),
      // Only 'pending' is currently sent by the backend. 'accepted' and 'timed_out' are speculative.
      transfer_status: z.enum(['pending', 'accepted', 'timed_out']),
      receiving_agency_display_name: z.string().optional(),
      receiving_agency_identifier: z.string().optional(),
    })
    .passthrough(),
});

const conversationStatuses = new Set([
  CONVERSATION_START,
  CONVERSATION_UPDATE,
  CONVERSATION_END,
  CONVERSATION_TRANSFER,
]);

const normalizeLocation = (body: ConversationMessage['body']) => {
  const baseLocation = {
    caller_id: body.caller_id,
    call_start_time: body.conversation_start_time,
    source: body.source,
  };

  if ('location_data' in body) {
    return {
      ...baseLocation,
      ...body.location_data,
    };
  }

  return baseLocation;
};

const isConversationMessage = (
  message: StreamWSMessage,
): message is ConversationMessage => {
  return conversationStatuses.has(message.type);
};

const useHandleConversationMessage = () => {
  const StreamWS = window?.__RSOS?.streamWS?.instance;
  const dispatch = useDispatch();
  const hasAdminLineRequests = useHasAccessToAdminLineRequests();
  const isTCAPIStreamWS = useFlag('tcapi_streamWS');

  const handleConversationMessage = useCallback(
    (message: StreamWSMessage) => {
      if (isConversationMessage(message)) {
        const body = message.body;
        const source = body.source;
        const isUnAuthAdmin =
          source === SOURCE_ADMIN_SMS && !hasAdminLineRequests;
        const isTCAPINotAllowed = source === SOURCE_TCAPI && !isTCAPIStreamWS;

        if (isUnAuthAdmin || isTCAPINotAllowed) {
          if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console -- dev-only diagnostics
            console.debug(
              `Ignoring conversation message from source ${source} due to ${isUnAuthAdmin ? 'lack of admin line requests access' : 'tcapi_streamWS flag being disabled'}`,
            );
          }
          return;
        }

        const normalizedLocation = normalizeLocation(body);
        dispatch(addLocToQueue(normalizedLocation));

        let payload = body;
        if (source === SOURCE_TCAPI) {
          const snakeCaseExtraInfo = convertKeysToCamelCase(body.extra_info);
          payload = { ...body, ...snakeCaseExtraInfo };

          if (message.type === CONVERSATION_UPDATE) {
            dismissToastByID(`conversations-toast-${body.caller_id}`);
          }
        }

        if (message.type === CONVERSATION_TRANSFER) {
          const parsed = conversationTransferMessageSchema.safeParse(message);
          if (!parsed.success) {
            // eslint-disable-next-line no-console -- logging for sentry
            console.error(
              'Failed to parse conversation.transfer message:',
              parsed.error,
            );
            return;
          }
          dispatch(
            setConversationTransfer({
              callerID: parsed.data.body.caller_id,
              transfer: {
                status: parsed.data.body.transfer_status,
                receivingAgencyName:
                  parsed.data.body.receiving_agency_display_name ?? null,
              },
            }),
          );
          return;
        }

        if (source === SOURCE_RCS) {
          dispatch(addRCSConversation(body.session_metadata));
        } else if (message.type === CONVERSATION_START) {
          dispatch(addConversation(payload));
        } else if (
          message.type === CONVERSATION_UPDATE ||
          message.type === CONVERSATION_END
        ) {
          dispatch(
            updateConversation({ body: payload, messageType: message.type }),
          );
        }
      }
    },
    [dispatch, hasAdminLineRequests, isTCAPIStreamWS],
  );

  useEffect(() => {
    if (StreamWS) {
      StreamWS.addOnMessageCallback({
        handleConversationMessage: handleConversationMessage as unknown as (
          message: Record<string, unknown>,
        ) => void,
      });

      return () => {
        StreamWS.removeOnMessageCallback('handleConversationMessage');
      };
    }
  }, [StreamWS, handleConversationMessage]);
};

export default useHandleConversationMessage;
